import React, { useState, useEffect, useRef, startTransition } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Language, Package, PackageContent } from "../../Types/types";
import ImageIcon from "@mui/icons-material/Image";
import CircularProgress from "@mui/material/CircularProgress";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideocamIcon from "@mui/icons-material/Videocam";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import PanoramaPhotosphereSelectIcon from "@mui/icons-material/PanoramaPhotosphereSelect";
import { useTheme, useMediaQuery } from "@mui/material";
import { useLanguage } from "../LanguageContext";
import IconWithStyle from "./iconWithStyle";
import StepItem from "./StepItem";
import ContentRenderer from "./contentRenderer";
import Button from "../Button";
import DescriptionModal from "./descriptionModal";
import translationManualDetail from "./translationManualDetail";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../Manual_List/fetch/store";
import {
  fetchStepThumbnail,
  fetchPackageById,
  fetchContentById,
  fetchSharedPackageById,
  fetchSharedStepThumbnail,
  fetchSharedContentById,
} from "../Manual_List/fetch/slices";
import { cleanBase64ImageData } from "../Manual_List/encodeImage";
import { SHARED_TOKEN_QUERY_PARAM } from "../../utils/sharedAccess";
import logo from "../../assets/ar encyclopedy.png";

const ManualDetail: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [pdfWidth, setPdfWidth] = useState<number | null>(null);
  const [guideLabel, setGuideLabel] = useState<string>("");
  const [stepsData, setStepsData] = useState<PackageContent[]>([]);
  const [manualDescription, setManualDescription] = useState<string>("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [contentUrl, setContentUrl] = useState<string>("");
  const [fileSize, setFileSize] = useState<string>("");
  const [animationControls, setAnimationControls] = useState<{
    hasExplodedAnimation: boolean;
    isDisassembled: boolean;
    isAnimating: boolean;
    isSpinning: boolean;
    handleDisassemble: () => void;
    handleAssemble: () => void;
    handle360Spin: () => void;
  } | null>(null);

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const thumbnails = useSelector(
    (state: RootState) => state.data.thumbnailUrl.data
  );

  const { guideId } = useParams<{ guideId: string }>();
  const [searchParams] = useSearchParams();
  const sharedToken = searchParams.get(SHARED_TOKEN_QUERY_PARAM);
  const contentRef = useRef<HTMLDivElement>(null);
  const [filterType, setFilterType] = useState<string[]>([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = translationManualDetail[language as Language];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const response = await dispatch(
          sharedToken
            ? fetchSharedPackageById({
                id: guideId!,
                locale: language,
                token: sharedToken,
              })
            : fetchPackageById({ id: guideId!, locale: language })
        );
        const guideData = response.payload as Package;

        if (!guideData) {
          throw new Error(`Guide with ID ${guideId} not found`);
        }

        startTransition(() => {
          setGuideLabel(guideData.label);
          setManualDescription(guideData.description);
          setStepsData(guideData.packageContents || []);

          if (
            guideData.packageContents &&
            guideData.packageContents.length > 0
          ) {
            const firstStep = guideData.packageContents[0];
            setActiveStep(firstStep.id);
          }
        });
      } catch (error) {
        console.error("Failed to load guide details", error);
        // An auth failure (401) on the normal (non-shared) path is already
        // handled by the axios interceptor / JwtExpirationHandlerComponent,
        // which redirects to /login while preserving this URL as the
        // post-login destination. Only redirect home here for genuine
        // failures (guide not found, bad/expired shared token, etc.) —
        // i.e. when the user is authenticated, or this was a shared-link
        // request (which has no login fallback to defer to).
        if (sharedToken || localStorage.getItem("token")) {
          navigate(`/`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [guideId, navigate, language, dispatch, sharedToken]);

  useEffect(() => {
    if (activeStep != null) {
      const thumbnailPromise = sharedToken
        ? dispatch(
            fetchSharedStepThumbnail({
              id: activeStep.toString(),
              token: sharedToken,
            })
          )
        : dispatch(fetchStepThumbnail({ id: activeStep.toString() }));
      thumbnailPromise.then(() => {});
    }
  }, [activeStep, dispatch, sharedToken]);

  useEffect(() => {
    const updateDimensions = () => {
      const drawerWidth = isDrawerOpen ? 240 : 0;
      const reductionPercentage = 0.75;

      if (contentRef.current) {
        const availableWidth = window.innerWidth - drawerWidth;
        const adjustedWidth = availableWidth * reductionPercentage;
        setPdfWidth(adjustedWidth - 20);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [isDrawerOpen]);

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const goBack = () => {
    const state = sessionStorage.getItem("manualsListState");
    if (state) {
      const { search, itemsPerPage, page, categories, subCategories, order } =
        JSON.parse(state);
      navigate(
        `/?search=${search}&itemsPerPage=${itemsPerPage}&page=${page}&categories=${categories}&subCategories=${subCategories}&order=${order}&lang=${language}&fromDetail=true`
      );
    } else {
      navigate(`/`);
    }
  };

  const handleStepClick = (id: number) => {
    startTransition(() => {
      setActiveStep(id);
      setContentUrl("");
      toggleDrawer();
    });
  };

  const handleIconClick = (type: string) => {
    setFilterType((current) => {
      const index = current.indexOf(type);
      if (index >= 0) {
        return current.filter((t) => t !== type);
      } else {
        return [...current, type];
      }
    });
  };

  const nextPanorama = (nextImagePath: string) => {
    const nextStep = stepsData.find((step) => step.source === nextImagePath);
    if (nextStep) {
      startTransition(() => {
        setActiveStep(nextStep.id);
      });
    }
  };

  const handleNextStep = async () => {
    if (activeStep !== null) {
      const currentIndex = stepsData.findIndex(
        (step) => step.id === activeStep
      );
      if (currentIndex < stepsData.length - 1) {
        const nextStepId = stepsData[currentIndex + 1].id;
        startTransition(() => {
          setActiveStep(nextStepId);
          setContentUrl("");
        });
      }
    }
  };

  const handlePreviousStep = async () => {
    if (activeStep !== null) {
      const currentIndex = stepsData.findIndex(
        (step) => step.id === activeStep
      );
      if (currentIndex > 0) {
        const previousStepId = stepsData[currentIndex - 1].id;
        startTransition(() => {
          setActiveStep(previousStepId);
          setContentUrl("");
        });
      }
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const filteredSteps =
    filterType.length > 0
      ? stepsData.filter((step) => filterType.includes(step.type))
      : stepsData;

  const fullscreenClass = isFullscreen
    ? "fixed inset-0 z-50 bg-black"
    : "flex flex-col min-h-screen bg-gray-100";

  const activeStepData = stepsData.find((s) => s.id === activeStep);
  const isObjectStep = activeStepData?.type === "object";

  const handleStepUpdate = async (newStepId: number) => {
    setActiveStep(newStepId);
    setLoading(true);

    try {
      const contentResponse = await dispatch(
        sharedToken
          ? fetchSharedContentById({
              id: newStepId.toString(),
              token: sharedToken,
            })
          : fetchContentById({ id: newStepId.toString() })
      );
      const { url, size } = contentResponse.payload as {
        url: string;
        size: string;
      };
      setContentUrl(url);
      setFileSize(size);
    } catch (error) {
      console.error("Failed to fetch content for the new step", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${fullscreenClass} transition-all duration-300 ease-in-out dark:bg-black`}
    >
      {!isFullscreen && (
        <>
          <div className="flex sm:flex-row sm:items-center flex-col ml-4 ">
            <img
              src={logo}
              alt="Logo"
              className="h-8 sm:block hidden w-auto cursor-pointer mb-4"
            />

            <div className="sm:p-4 sm:mb-4 sm:mt-1 mt-5 whitespace-normal break-word sm:max-w-[500px] max-w-[240px]">
              <h1 className="font-semibold sm:text-3xl text-xl text-purple">
                {guideLabel || "Step Detail"}
              </h1>
            </div>
          </div>

          <div className="absolute top-4 right-4 z-20 flex items-center gap-4">
            {!isDrawerOpen &&
              isObjectStep &&
              contentUrl !== "" &&
              animationControls && (
                <>
                  <button
                    onClick={animationControls.handle360Spin}
                    disabled={
                      animationControls.isSpinning ||
                      animationControls.isAnimating
                    }
                    className={`p-2 rounded-lg font-semibold transition-all ${
                      animationControls.isSpinning ||
                      animationControls.isAnimating
                        ? "bg-gray-400 text-gray-600 cursor-not-allowed opacity-50"
                        : "bg-purple text-white hover:bg-darkPurple cursor-pointer"
                    }`}
                    title={
                      animationControls.isSpinning
                        ? "Spinning..."
                        : animationControls.isAnimating
                        ? "Disabled during animation"
                        : "360° Spin"
                    }
                  >
                    {animationControls.isSpinning ? (
                      <svg
                        className="animate-spin h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    )}
                  </button>

                  {animationControls.hasExplodedAnimation && (
                    <button
                      onClick={() => {
                        setAnimationControls((prev) =>
                          prev ? { ...prev, isAnimating: true } : prev
                        );
                        if (animationControls.isDisassembled) {
                          animationControls.handleAssemble();
                        } else {
                          animationControls.handleDisassemble();
                        }
                      }}
                      disabled={
                        animationControls.isAnimating ||
                        animationControls.isSpinning
                      }
                      className={`p-2 rounded-lg font-semibold transition-all ${
                        animationControls.isAnimating ||
                        animationControls.isSpinning
                          ? "bg-gray-400 text-gray-600 cursor-not-allowed opacity-50"
                          : "bg-purple text-white hover:bg-darkPurple cursor-pointer"
                      }`}
                      title={
                        animationControls.isAnimating
                          ? "Animating..."
                          : animationControls.isSpinning
                          ? "Disabled during 360° spin"
                          : animationControls.isDisassembled
                          ? "Assemble"
                          : "Disassemble"
                      }
                    >
                      {animationControls.isAnimating ? (
                        <svg
                          className="animate-spin h-6 w-6"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : animationControls.isDisassembled ? (
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                      )}
                    </button>
                  )}
                </>
              )}
            {!isDrawerOpen && (
              <img
                src="/Icons/infoIcon.svg"
                onClick={handleModalOpen}
                className="cursor-pointer text-gray-800 hover:text-gray-600 transition-colors dark:text-white size-6"
                alt="Information icon"
              />
            )}
            <MenuIcon
              onClick={toggleDrawer}
              className="cursor-pointer text-gray-800 hover:text-gray-600 transition-colors dark:text-white"
              fontSize="large"
            />
          </div>

          <div
            className={`fixed inset-y-0 right-0 transform transition-transform duration-300 ease-in-out z-10 ${
              isDrawerOpen ? "translate-x-0" : "translate-x-full"
            } bg-white dark:bg-darkBackground shadow-xl p-4 pb-8 md:w-96 w-full max-h-screen flex flex-col`}
          >
            <div className="flex items-center mt-1">
              <div
                className="flex gap-2 items-center cursor-pointer group"
                onClick={goBack}
              >
                <ArrowBackIosNewIcon className="text-gray-800 hover:text-gray-600 mr-auto bg-lightPurple p-2 rounded-md group-[]:hover:opacity-70 transition-all" />
                <p className="text-xl font-light dark:text-white">Back</p>
              </div>
            </div>

            <div className="sticky top-0 bg-white dark:bg-darkBackground z-20 pb-4">
              <div className="flex justify-between gap-4 sm:gap-12 my-6">
                <IconWithStyle
                  IconComponent={ImageIcon}
                  type="image"
                  isSelected={filterType.includes("image")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={PictureAsPdfIcon}
                  type="pdf"
                  isSelected={filterType.includes("pdf")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={VideocamIcon}
                  type="video"
                  isSelected={filterType.includes("video")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={ViewInArIcon}
                  type="object"
                  isSelected={filterType.includes("object")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={PanoramaPhotosphereSelectIcon}
                  type="panorama"
                  isSelected={filterType.includes("panorama")}
                  handleIconClick={handleIconClick}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto hide-scrollbar">
              <div className="space-y-3">
                {filteredSteps.map((step) => (
                  <StepItem
                    key={step.id}
                    step={step}
                    handleStepClick={handleStepClick}
                    language={language}
                  />
                ))}
              </div>
            </div>
          </div>

          <DescriptionModal
            open={isModalOpen}
            handleClose={handleModalClose}
            description={manualDescription}
            language={language}
          />
        </>
      )}

      <div
        ref={contentRef}
        className={`flex-grow flex flex-col justify-center items-center p-4 text-black transition-margin duration-300 ease-in-out max-w-full`}
        style={
          isSmallScreen || isMediumScreen
            ? { maxHeight: "calc(100vh - 160px)" }
            : {}
        }
      >
        {loading ? (
          <CircularProgress />
        ) : (
          activeStep && (
            <ContentRenderer
              step={{
                ...stepsData.find((s) => s.id === activeStep),
                thumbnail: cleanBase64ImageData(thumbnails[activeStep]),
              }}
              isDrawerOpen={isDrawerOpen}
              handleThumbnailClick={() => handleStepUpdate(activeStep!)}
              showThumbnail={contentUrl === ""}
              pdfWidth={pdfWidth}
              nextPanorama={nextPanorama}
              contentUrl={contentUrl!}
              fileSize={fileSize}
              setIsFullscreen={setIsFullscreen}
              updateContentUrl={setContentUrl}
              handleStepUpdate={handleStepUpdate}
              setLoading={setLoading}
              onAnimationControlsChange={setAnimationControls}
              sharedToken={sharedToken}
            />
          )
        )}
      </div>

      {!isFullscreen && (
        <div className="flex flex-col sm:mt-4 sm:space-y-2 sm:items-center sm:p-4 bg-gray-100 p-4 fixed bottom-0 left-0 right-0 sm:static sm:bg-transparent">
          <Button
            buttonText={t.previous}
            onClick={handlePreviousStep}
            className={`w-full sm:max-w-xs ${
              activeStep === stepsData[0]?.id
                ? "cursor-not-allowed opacity-50 bg-purple text-white"
                : "bg-darkPurple hover:bg-purple text-white"
            }`}
          />
          <Button
            buttonText={t.next}
            onClick={handleNextStep}
            className={`w-full sm:max-w-xs mt-2 ${
              activeStep === stepsData[stepsData.length - 1]?.id
                ? "cursor-not-allowed opacity-50 bg-purple text-white"
                : "bg-darkPurple hover:bg-purple text-white"
            }`}
          />
        </div>
      )}
    </div>
  );
};

export default ManualDetail;
